<template>
  <div>
    <div class="ch-head">
      <div>
        <h1>协议与保证金</h1>
        <p>协议线下签署后上传扫描件，上传即归档。保证金只作资格押金，不能抵扣积分。</p>
      </div>
      <div class="ch-actions">
        <button class="ch-btn" type="button" @click="fileOpen = true">上传协议扫描件</button>
        <button class="ch-btn gold" type="button" @click="depositOpen = true">补缴保证金</button>
      </div>
    </div>
    <div class="ch-metrics three">
      <div class="ch-metric"><div class="k">应缴金额</div><div class="v">¥ {{ yuan(standard) }}</div><div class="s">平台要求缴纳 · 已确认 ¥ {{ yuan(balance) }} · 还需补缴 ¥ {{ yuan(shortfall) }}</div></div>
      <div class="ch-metric"><div class="k">已签署协议</div><div class="v">{{ signed }}</div><div class="s">共 {{ agreements.length }} 份</div></div>
      <div class="ch-metric"><div class="k">待确认到账</div><div class="v">{{ pendingPay }}</div><div class="s">补缴后等总部确认</div></div>
    </div>
    <div class="ch-panel">
      <div class="ch-tabs">
        <button type="button" :class="{ on: tab === 'file' }" @click="tab = 'file'">我的协议</button>
        <button type="button" :class="{ on: tab === 'cash' }" @click="tab = 'cash'">保证金流水</button>
      </div>
      <table v-if="tab === 'file'" class="ch-table">
        <thead><tr><th>协议</th><th>扫描件</th><th>有效期</th><th>状态</th></tr></thead>
        <tbody>
          <tr v-for="row in agreements" :key="row.id">
            <td>{{ row.name }}</td>
            <td><a v-if="row.fileUrl" :href="row.fileUrl" target="_blank" rel="noopener">查看 PDF</a><span v-else>未上传</span></td>
            <td>{{ row.validUntil || '—' }}</td>
            <td><span class="ch-tag" :class="fileTag(row.status)">{{ fileStatus(row.status) }}</span></td>
          </tr>
          <tr v-if="!agreements.length"><td colspan="4" class="ch-empty">还没有协议</td></tr>
        </tbody>
      </table>
      <table v-else class="ch-table">
        <thead><tr><th>时间</th><th>方向</th><th>金额</th><th>状态</th><th>备注</th></tr></thead>
        <tbody>
          <tr v-for="row in deposits" :key="row.id">
            <td>{{ row.createdAt }}</td>
            <td>{{ row.direction === 'OUT' ? '退还' : '缴纳' }}</td>
            <td>{{ row.direction === 'OUT' ? '-' : '+' }}{{ yuan(row.amount) }}</td>
            <td><span class="ch-tag" :class="row.status === 'CONFIRMED' ? 'ok' : 'warn'">{{ row.status === 'CONFIRMED' ? '已确认' : '待确认' }}</span></td>
            <td>{{ row.remark || '—' }}</td>
          </tr>
          <tr v-if="!deposits.length"><td colspan="5" class="ch-empty">还没有保证金流水</td></tr>
        </tbody>
      </table>
    </div>
    <el-dialog title="补缴保证金" :visible.sync="depositOpen" width="420px">
      <el-form label-width="80px" size="small">
        <el-form-item label="当前余额">¥ {{ yuan(balance) }}</el-form-item>
        <el-form-item label="应缴标准">¥ {{ yuan(standard) }}</el-form-item>
        <el-form-item label="金额"><el-input v-model="deposit.amount" /></el-form-item>
        <el-form-item label="备注"><el-input v-model="deposit.remark" placeholder="转账说明" /></el-form-item>
      </el-form>
      <div slot="footer"><el-button size="small" type="primary" @click="pay">提交申请</el-button></div>
    </el-dialog>
    <el-dialog title="上传协议扫描件" :visible.sync="fileOpen" width="460px">
      <input ref="pdf" class="file-proxy" type="file" accept="application/pdf,.pdf" @change="onPdf" />
      <el-form label-width="80px" size="small">
        <el-form-item label="协议文件">
          <button class="ch-btn" type="button" @click="pickPdf">选择 PDF</button>
          <span v-if="file.name" class="file-name">{{ file.name }}.pdf</span>
        </el-form-item>
        <el-form-item label="协议名"><el-input :value="file.name" readonly placeholder="选择 PDF 后自动填入" /></el-form-item>
        <el-form-item label="有效期"><el-input v-model="file.validUntil" placeholder="2027-03-17" /></el-form-item>
      </el-form>
      <p class="ch-note">只能上传 PDF。协议名取文件名，提交后等总部审批。</p>
      <div slot="footer"><el-button size="small" type="primary" :loading="uploading" @click="upload">确认上传</el-button></div>
    </el-dialog>
  </div>
</template>

<script>
import { getChannelAgreements, saveChannelAgreement, getChannelDeposits, saveChannelDeposit } from '@/api/admin'
import { uploadFile } from '@/api/upload'
import { normalizeFileUrl } from '@/utils/fileUrl'
import { yuan } from './labels'

export default {
  name: 'PartnerCompliance',
  data() {
    return {
      tab: 'file', agreements: [], deposits: [], balance: 0, standard: 0,
      depositOpen: false, fileOpen: false, uploading: false, picking: false,
      deposit: { amount: '', remark: '' },
      file: { name: '', fileUrl: '', validUntil: '' }
    }
  },
  computed: {
    signed() { return this.agreements.filter(item => item.status === 'SIGNED').length },
    pendingPay() { return this.deposits.filter(item => item.status === 'PENDING').length },
    enough() { return Number(this.balance) >= Number(this.standard || 0) }
  },
  created() { this.load() },
  methods: {
    yuan,
    fileStatus(status) {
      if (status === 'SIGNED') return '已通过'
      if (status === 'PENDING_REVIEW') return '待审批'
      if (status === 'REJECTED') return '已驳回'
      return '待上传'
    },
    fileTag(status) {
      if (status === 'SIGNED') return 'ok'
      if (status === 'REJECTED') return 'bad'
      return 'warn'
    },
    pickPdf() {
      if (this.picking) return
      this.picking = true
      const input = this.$refs.pdf
      if (input) input.click()
      window.setTimeout(() => { this.picking = false }, 800)
    },
    async onPdf(event) {
      const picked = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!picked) return
      const name = String(picked.name || '')
      if (!/\.pdf$/i.test(name)) {
        this.$message.warning('只能上传 PDF')
        return
      }
      if (picked.size > 20 * 1024 * 1024) {
        this.$message.warning('PDF 不能超过 20MB')
        return
      }
      this.uploading = true
      try {
        const result = await uploadFile(picked, 'agreement', { useAdmin: true })
        this.file.fileUrl = normalizeFileUrl(result.url)
        this.file.name = name.replace(/\.pdf$/i, '')
      } finally {
        this.uploading = false
      }
    },
    async load() {
      const agreements = await getChannelAgreements()
      const deposits = await getChannelDeposits()
      this.agreements = agreements.list || []
      this.deposits = deposits.list || []
      this.balance = deposits.balance || 0
      this.standard = deposits.standard || 0
      this.shortfall = deposits.shortfall || 0
    },
    openDeposit() {
      this.deposit = { amount: Number(this.shortfall) > 0 ? String(this.shortfall) : '', remark: '' }
      this.depositOpen = true
    },
    async pay() {
      await saveChannelDeposit(this.deposit)
      this.depositOpen = false
      this.$message.success('已提交，等总部确认到账')
      this.load()
    },
    async upload() {
      if (!this.file.fileUrl || !/\.pdf(\?|$)/i.test(this.file.fileUrl)) {
        this.$message.warning('请先选择 PDF')
        return
      }
      this.uploading = true
      try {
        await saveChannelAgreement(this.file)
        this.fileOpen = false
        this.file = { name: '', fileUrl: '', validUntil: '' }
        this.$message.success('已提交，等总部审批')
        this.load()
      } finally {
        this.uploading = false
      }
    }
  }
}
</script>

<style scoped>
.file-proxy { position: fixed; left: 0; top: 0; width: 1px; height: 1px; opacity: 0; overflow: hidden; pointer-events: none; }
.file-name { margin-left: 8px; color: #5c6780; font-size: 13px; }
</style>
